import { scheduleCrud } from "@/api/schedule";
import type { PhotoService, ScheduleDetail, ScheduleStage, UpdateScheduleStagePayload } from "@/api/schedule/models";
import { UserRoleEnum } from "@/api/user/models";
import { InputDropdown } from "@/components/common/inputs";
import { InputReadonly } from "@/components/common/inputs/InputReadonly";
import { ModalAlert } from "@/components/common/modals";
import { SCHEDULE_STAGE_LABEL_BY_STAGE } from "@/constants/schedule";
import { useDepartmentNavigate } from "@/hooks/common/use-department-navigate";
import { useDepartmentRoute } from "@/hooks/common/use-department-route";
import { useFetch } from "@/hooks/common/use-fetch";
import { useForm } from "@/hooks/common/use-form";
import { showToast } from "@/providers";
import { RoutePathEnum } from "@/routes/paths";
import { useUserStore } from "@/stores/use-user-store";
import { getUserRoleByDepartment } from "@/utils/user-positions";
import { useQueryClient } from "@tanstack/react-query";
import * as yup from "yup";

type MoveScheduleStageModalProps = {
  isOpen: boolean;
  schedule: ScheduleDetail;
  onClose: () => void;
};

const photoServiceOptions: { value: PhotoService; label: string }[] = [
  { value: "PHOTO_MATERIAL", label: "Material fotográfico" },
  { value: "PHOTOGENY", label: "Fotogenia" },
];

export const MoveScheduleStageModal = ({ isOpen, schedule, onClose }: MoveScheduleStageModalProps) => {
  const queryClient = useQueryClient();
  const isPhotoRenewal = schedule.scheduleType === "PHOTO_RENEWAL";
  const activeUser = useUserStore((state) => state.activeUser);
  const { department } = useDepartmentRoute();
  const departmentNavigate = useDepartmentNavigate();
  const activeRole = getUserRoleByDepartment(activeUser, department);
  const canSelectProducer = [UserRoleEnum.ADMINISTRATOR, UserRoleEnum.COORDINATOR].includes(activeRole as UserRoleEnum);
  const canMoveToProduction = canSelectProducer || activeRole === UserRoleEnum.PRODUCER;

  const stageOptions = (Object.entries(SCHEDULE_STAGE_LABEL_BY_STAGE) as [ScheduleStage, string][]).reduce(
    (entries, [stage, label]) => {
      if (stage === "PRODUCTION" && !canMoveToProduction) {
        return entries;
      }

      if (stage === "PHOTOGRAPHY" && isPhotoRenewal && schedule.currentStage === "PHOTOGRAPHY") {
        return entries;
      }

      if (stage === "PHOTOGRAPHY" || (stage !== "SCHEDULED" && stage !== schedule.currentStage)) {
        entries.push({ value: stage, label });
      }

      return entries;
    },
    [] as { value: ScheduleStage; label: string }[],
  );

  const availablePhotoServiceOptions = photoServiceOptions.filter(({ value }) => {
    if (isPhotoRenewal) {
      return value === "PHOTO_MATERIAL";
    }

    return schedule.currentStage !== "PHOTOGRAPHY" || value !== schedule.photoService;
  });

  const { action, control, isSubmitting } = useForm({
    initialValues: {
      photoService: isPhotoRenewal ? "PHOTO_MATERIAL" : undefined,
      responsibleProducerId: undefined,
    },
    handleSubmit: async ({ stage, photoService, responsibleProducerId }: UpdateScheduleStagePayload) => {
      const payload: UpdateScheduleStagePayload = { stage };

      if (stage === "PHOTOGRAPHY") {
        payload.photoService = photoService;
      }

      if (stage === "PRODUCTION") {
        const producerId = activeRole === UserRoleEnum.PRODUCER ? activeUser?.id : responsibleProducerId;
        if (!canMoveToProduction || !producerId) {
          throw new Error("Selecione o produtor responsável.");
        }
        payload.responsibleProducerId = producerId;
      }

      await scheduleCrud.updateStage(schedule.id, payload);
      return null;
    },
    fields: {
      stage: { validation: "required" },
      responsibleProducerId: {
        validation: yup
          .string()
          .transform((value) => (value === "" ? undefined : value))
          .when("stage", {
            is: (stage: ScheduleStage) => stage === "PRODUCTION" && canSelectProducer,
            then: (schema) => schema.required("Selecione o produtor responsável."),
            otherwise: (schema) => schema.notRequired(),
          }),
      },
      photoService: {
        validation: yup.string().when("stage", {
          is: "PHOTOGRAPHY",
          then: (schema) => schema.required("Selecione o serviço de fotografia."),
          otherwise: (schema) => schema.notRequired(),
        }),
      },
    },
    options: {
      onSuccess: async () => {
        showToast({ type: "success", title: "Etapa alterada com sucesso!" });
        handleOnClose();

        await queryClient.invalidateQueries({
          predicate: ({ queryKey }) => {
            const [scope, resource, scheduleId] = queryKey;

            if (scope !== "schedule") {
              return false;
            }

            if (
              [UserRoleEnum.ADMINISTRATOR, UserRoleEnum.COORDINATOR, UserRoleEnum.RECEPTIONIST].includes(
                activeRole as UserRoleEnum,
              )
            ) {
              return true;
            }

            if (resource === "detail" && scheduleId === schedule.id) {
              return false;
            }

            return true;
          },
          refetchType: "all",
        });

        if (
          ![UserRoleEnum.ADMINISTRATOR, UserRoleEnum.COORDINATOR, UserRoleEnum.RECEPTIONIST].includes(
            activeRole as UserRoleEnum,
          )
        ) {
          departmentNavigate(RoutePathEnum.SCHEDULE);
        }
      },
    },
  });

  const selectedStage = control.watch("stage");

  // TODO: Substituir se quantidade de produtores for grande ou após InputDropdown suportar paginação
  const { data: producers } = useFetch(
    () => scheduleCrud.getProducers(schedule.id, { sort: { page: 1, limit: 100 } }),
    {
      queryKey: ["schedule", schedule.id, "producers"],
      includeDepartmentInCache: true,
      enabled: isOpen && selectedStage === "PRODUCTION" && canSelectProducer,
      refetchInterval: isOpen && selectedStage === "PRODUCTION" && canSelectProducer ? 5 * 60 * 1000 : false,
    },
  );

  const producerOptions =
    producers?.data.map(({ id, name, isAvailable }) => ({
      value: id,
      label: name ?? "Sem nome",
      disabled: !isAvailable,
    })) ?? [];

  function handleOnClose() {
    onClose();

    control.reset({
      stage: undefined,
      photoService: isPhotoRenewal ? "PHOTO_MATERIAL" : undefined,
      responsibleProducerId: undefined,
    });
  }

  return (
    <ModalAlert
      title="Mover para outra etapa"
      description="Selecione a nova etapa para o talento."
      isOpen={isOpen}
      onClose={isSubmitting ? undefined : handleOnClose}
      isOverlayCloseable={!isSubmitting}
      cancelable
      actions={[
        {
          type: "submit",
          form: "move-schedule-stage-form",
          children: "Confirmar mudança",
          isLoading: isSubmitting,
          disabled: selectedStage === "PRODUCTION" && activeRole === UserRoleEnum.PRODUCER && !activeUser?.id,
        },
      ]}
    >
      <form className="form-flow" onSubmit={action} id="move-schedule-stage-form">
        <InputReadonly label="Etapa atual" value={SCHEDULE_STAGE_LABEL_BY_STAGE[schedule.currentStage]} />

        <InputDropdown field="stage" label="Nova etapa" options={stageOptions} control={control} />

        {selectedStage === "PRODUCTION" && canSelectProducer && (
          <InputDropdown
            field="responsibleProducerId"
            label="Produtor responsável"
            options={producerOptions}
            control={control}
          />
        )}

        {selectedStage === "PHOTOGRAPHY" && (
          <InputDropdown
            field="photoService"
            label="Serviço"
            options={availablePhotoServiceOptions}
            control={control}
          />
        )}
      </form>
    </ModalAlert>
  );
};
